import React, { useEffect, useState } from 'react';
import DynamicTable from '@/components/DynamicTable';
import { _newLine, isValidDate, makeTree, renderTelesaleDeadline, renderTelesaleTime, telesaleFollowStatuses } from '@/utils/helpers';
import HtmlParser from 'react-html-parser';
import { Tag, Modal, Form, Input, DatePicker, message, Button, TreeSelect, Radio, Row, Col, Space } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { updateResultCustomerConsultantDirect } from '@/redux/actions/CustomerConsultantDirectActions';
import { getTelesaleFollowUpsByCustomer, assignedTelesaleResult } from '@/redux/actions/telesaleFollowUpActions';
import BaseSelect from '@/components/Elements/BaseSelect';
import { fetchFilters } from '@/redux/actions/filterActions';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import TelesaleManualResultForm from '@/routes/telesale-follow-ups/TelesaleManualResultForm';
import { checkPermission } from '@/utils/permission';

const { TextArea } = Input;

const ConsultationList = ({ customer }) => {

    const customerId = customer?.id || null;

    const dispatch = useDispatch();

    /** Lấy dữ liệu từ reducer */
    const telesaleFollowUps = useSelector(state => state.telesaleFollowUpReducer.telesaleFollowUpsByCustomer || []);
    const filterData = useSelector(state => state.filterReducer.list);
    const services = makeTree(filterData?.services || [], 0);

    const [isLoading, setIsLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [openManualResult, setOpenManualResult] = useState(false);
    const [form] = Form.useForm();

    const isTelesale = selectedRecord?.care_type === 'telesale';
    const status = Form.useWatch('status', form);
    const isNotDone = status === 0;

    const allowUpdateResult = checkPermission('telesale_follow_up', 'update_result');

    /** Load danh sách Telesale theo customer_id */
    useEffect(() => {
        if (customerId) {
            dispatch(fetchFilters());
            dispatch(getTelesaleFollowUpsByCustomer(customerId));
        }
    }, [customerId]);

    const openResultModal = (record) => {
        setSelectedRecord(record);
        form.resetFields();

        form.setFieldsValue({
            time_care: record.time_care ? moment(record.time_care) : null,
            result_content: _newLine(record.result_content) || '',
            status: record.status || 0,
            service_id: record.service_id || null,
            follow_status: record.follow_status || null
        });

        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = form.getFieldsValue();
            const payload = { ...values };

            if (values.status === 0) {
                delete payload.time_care;
                delete payload.service_id;
                delete payload.follow_status;
                delete payload.result_content;
            }

            if (selectedRecord.care_type === "direct") {
                payload.time_consultant = values.time_care
                    ? values.time_care.format("YYYY-MM-DD HH:mm:ss")
                    : null;

                delete payload.time_care;

                await dispatch(updateResultCustomerConsultantDirect(selectedRecord.id, payload));
            } else {
                payload.handled_at = values.time_care
                    ? values.time_care.format("YYYY-MM-DD HH:mm:ss")
                    : null;

                delete payload.time_care;

                await dispatch(assignedTelesaleResult(selectedRecord.id, payload));
            }

            setIsModalVisible(false);
            dispatch(getTelesaleFollowUpsByCustomer(customerId));

        } catch (err) {
            message.error("Có lỗi xảy ra!");
        }
    };

    const handleReloadData = async () => {
        try {
            setIsLoading(true);
            await dispatch(getTelesaleFollowUpsByCustomer(customerId));
            console.log('Reload customer data', customerId);
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to reload data:', error);
        }
    };

    const closeManualResult = () => {
        setOpenManualResult(false);
    };

    const columns = [
        {
            title: 'Ngày giao tư vấn',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '140px',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {record.created_at ? isValidDate(record.created_at, true) : "-"}
                    </div>
                    {/* Loại chăm sóc: telesale / direct */}
                    <div style={{ marginBottom: 4 }}>
                        {record.care_type === "direct" ? (
                            <Tag color="blue" style={{ padding: "2px 6px", fontSize: 11 }}>
                                Tư vấn trực tiếp
                            </Tag>
                        ) : (
                            <Tag color="purple" style={{ padding: "2px 6px", fontSize: 11 }}>
                                Telesale
                            </Tag>
                        )}
                    </div>

                    {record.status == 1 ? (
                        <Tag color="green" style={{ padding: "2px 6px", fontSize: 11 }}>
                            Đã trả kết quả
                        </Tag>
                    ) : (
                        <Tag color="red" style={{ padding: "2px 6px", fontSize: 11 }}>
                            Chưa trả kết quả
                        </Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Deadline',
            key: 'deadline',
            width: '150px',
            render: (_, record) =>
                record.care_type === "telesale"
                    ? renderTelesaleDeadline(record.deadline, record.status)
                    : <Tag color="default">Không áp dụng</Tag>
        },
        {
            title: 'Nhân viên tiếp nhận',
            key: 'handler',
            width: '180px',
            render: (_, record) => {

                const handler = record.handled_by || record.accepted_by;

                return (
                    <div style={{ lineHeight: "18px" }}>

                        {/* THỜI GIAN */}
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            {record.care_type === "direct"
                                ? (
                                    record.time_care
                                        ? moment(record.time_care).format("DD/MM/YYYY HH:mm")
                                        : "-"
                                )
                                : renderTelesaleTime(record.deadline, record.time_care)
                            }
                        </div>

                        {/* NHÂN VIÊN */}
                        <div className='mt-2'>
                            {handler ? (
                                <div>
                                    {handler.user_role?.name && (
                                        <span style={{ color: "#888" }}>
                                            {handler.user_role.name} -{" "}
                                        </span>
                                    )}
                                    <strong>{handler.full_name}</strong>
                                </div>
                            ) : (
                                <Tag color="default">Chưa tiếp nhận</Tag>
                            )}
                        </div>

                    </div>
                );
            }
        },
        {
            title: 'Nhân viên được phân công',
            key: 'time_care',
            width: '190px',
            render: (_, record) => (
                <div style={{ lineHeight: "18px" }}>
                    {/* NGƯỜI TƯ VẤN */}
                    <div className='mt-2'>
                        {record.staff && record.staff.length > 0 ? (
                            record.staff.map((s, idx) => (
                                <div key={idx}>
                                    {s.user?.user_role?.name || ""} - {s.user?.full_name}
                                </div>
                            ))
                        ) : (
                            <Tag color="default"></Tag>
                        )}
                    </div>

                </div>
            )
        },
        {
            title: 'Kết quả tư vấn',
            dataIndex: 'result_content',
            key: 'result_content',
            width: '350px',
            render: (text) => <>{HtmlParser(text)}</>
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 110,
            fixed: 'right',
            render: (_, record) => (
                <Button disabled={!allowUpdateResult} size="small" type="primary" onClick={() => openResultModal(record)}>
                    Trả kết quả
                </Button>
            )
        }
    ];

    return (
        <>
            <div className='mt-4 mb-4'>
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setOpenManualResult(true)}
                                disabled={!allowUpdateResult}
                            >
                                Trả kết quả telesale
                            </Button>
                            <Button
                                onClick={handleReloadData}
                                icon={<ReloadOutlined />}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>
            <div className='has-custom-column-table-btn'>
                <DynamicTable
                    dataSource={telesaleFollowUps || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={false}
                    rowSelection={false}
                    moduleKey="customer_care_list"
                    buttonStyle={{ top: 6 }}
                />
            </div>

            <TelesaleManualResultForm
                visible={openManualResult}
                customer={customer}
                loadData={() => handleReloadData()}
                onCancel={() => closeManualResult()}
            />

            <Modal
                title="Trả kết quả tư vấn"
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
                okText="Cập nhật"
                cancelText="Hủy"
                confirmLoading={isLoading}
                width={isMobile ? '100%' : '45%'}
            >
                <Form form={form} layout="vertical">

                    <Form.Item name="status" label="Trạng thái">
                        <Radio.Group>
                            <Radio value={1}>Trả kết quả</Radio>
                            <Radio value={0}>Chưa trả kết quả</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* CHỈ HIỂN THỊ KHI ĐÃ TRẢ KẾT QUẢ */}
                    {!isNotDone && (
                        <>
                            <Form.Item name="time_care" label="Thời gian tư vấn">
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>

                            {isTelesale && (
                                <>
                                    <Form.Item
                                        name="service_id"
                                        label="Dịch vụ quan tâm"
                                        rules={[
                                            { required: true, message: 'Vui lòng chọn dịch vụ!' }
                                        ]}
                                    >
                                        <TreeSelect
                                            showSearch
                                            className="w-100"
                                            dropdownStyle={{ maxHeight: 450, overflow: 'auto' }}
                                            treeData={services || []}
                                            treeNodeFilterProp="title"
                                            allowClear
                                            placeholder="Dịch vụ quan tâm"
                                            treeDefaultExpandAll
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="follow_status"
                                        label="Trạng thái follow"
                                        rules={[
                                            { required: true, message: 'Vui lòng chọn trạng thái follow!' }
                                        ]}
                                    >
                                        <BaseSelect
                                            options={telesaleFollowStatuses || []}
                                            optionValue="value"
                                            optionLabel="label"
                                            placeholder="Trạng thái follow"
                                        />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item name="result_content" label="Kết quả tư vấn">
                                <TextArea rows={8} placeholder="Nhập nội dung tư vấn..." />
                            </Form.Item>
                        </>
                    )}

                </Form>
            </Modal>
        </>
    );
};

export default ConsultationList;
